import { useMutation } from '@tanstack/react-query';
import * as React from 'react';

import type {
  ChatMessage,
  ChatResponse,
  FeedbackMessage,
} from '../../api/chat';
import { sendChatMessage } from '../../api/chat';
import type { ChatHistoryResponse } from '../../api/chat-history';
import { useBhanzuBuddy } from '../../api/queries';
import type { BhanzuBuddyChatMessage } from '../../components/bhanzu-buddy-chat/bhanzu-buddy-chat-types';
import { API_CONSTANTS } from '../../constants';

import { getErrorMessage } from './error-handling';

/**
 * API interaction utilities for BhanzuBuddy chat
 *
 * Handles API calls, mutations, and response processing for chat functionality.
 */

export interface UseApiInteractionsProps {
  leadId: string;
  source?: string;
  onMessageSent?: (response: ChatResponse) => void;
  onError?: (error: unknown) => void;
}

export const useApiInteractions = ({
  leadId,
  source = API_CONSTANTS.DEFAULT_SOURCE,
  onMessageSent,
  onError,
}: UseApiInteractionsProps) => {
  const { useGetChatHistory, useSendChatMessage, useSendFeedback } =
    useBhanzuBuddy();

  const sendFeedbackMutation = useSendFeedback();

  // Memoize chat history success callback
  const handleChatHistorySuccess = React.useCallback(
    (data: ChatHistoryResponse) => {
      // This will be handled by the parent hook
      return data;
    },
    []
  );

  // Memoize chat history error callback
  const handleChatHistoryError = React.useCallback(() => {
    onError?.(new Error('Failed to load chat history'));
  }, [onError]);

  // Get chat history
  const chatHistoryQuery = useGetChatHistory({
    params: {
      lead_id: leadId,
      source,
    },
    onSuccess: handleChatHistorySuccess,
    onError: handleChatHistoryError,
    // History is fetched explicitly on open; avoid duplicate fetch on mount
    enabled: false,
  });

  // Memoize send message success callback
  const handleSendMessageSuccess = React.useCallback(
    (data: ChatResponse) => {
      onMessageSent?.(data);
      return data;
    },
    [onMessageSent]
  );

  // Memoize send message error callback
  const handleSendMessageError = React.useCallback(
    (error: unknown) => {
      onError?.(error);
      return getErrorMessage(error);
    },
    [onError]
  );

  // Send chat message
  const sendMessageMutation = useSendChatMessage({
    onSuccess: handleSendMessageSuccess,
    onError: handleSendMessageError,
  });

  // Create a separate mutation for guide flow that doesn't auto-invalidate
  const guideMutation = useMutation({
    mutationFn: (data: ChatMessage) => sendChatMessage(data),
  });

  const handleFeedback = React.useCallback(
    (
      messageId: string,
      type: 'positive' | 'negative',
      messages: BhanzuBuddyChatMessage[]
    ) => {
      // Find the message to get its version number
      const message = messages.find(msg => msg.id === messageId);
      if (!message || message.type !== 'buddy' || !message.version) {
        // Silently return if message not found or missing version
        return;
      }

      const feedbackData: FeedbackMessage = {
        lead_id: leadId,
        feedback: type === 'positive',
        version: message.version,
      };
      sendFeedbackMutation.mutate(feedbackData);
    },
    [leadId, sendFeedbackMutation]
  );

  return {
    chatHistoryQuery,
    sendMessageMutation,
    guideMutation,
    sendFeedbackMutation,
    handleFeedback,
  };
};
